import { Grid, GridRow } from '@bp/ui-components';
import { Form, Formik, FormikHelpers } from 'formik';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useUserConfigContext } from '../../../hooks/useUserConfigContext';
import { showSuccessCreateToast, showSuccessUpdateToast, showUserDbErrorToast } from '../../../utils/toast';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import { useDeputateLessons } from '../hooks/useDeputateLessons';
import { LessonFormType } from '../types';
import { LessonBasicsForm } from './Basics/LessonBasicsForm';
import { LessonClassesForm } from './Classes/LessonClassesForm';
import { LessonUnitForm } from './LessonUnits/LessonUnitForm';
import { LessonRoomSupplyForm } from './RoomSupply/LessonRoomSupplyForm';
import { LessonTeacherForm } from './Teachers/LessonTeacherForm';
import { schema } from './validation/schema';

type LessonFormProps = {
  lessonUuid?: string | null;
  defaultInitialValues: Partial<LessonFormType>;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

export const DeputateLessonForm = observer(({ lessonUuid, defaultInitialValues, onLoad, onClose }: LessonFormProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const schoolYear = useUserConfigContext().selectedSchoolYear;

  const { createLesson, updateLesson, queryLessonsData, lessonsData } = useDeputateLessons({
    organizationUuid: pimAuthClaims.getOrganizationUuid(),
    schoolYearUuid: schoolYear?.uuid ?? '',
  });

  const lesson = queryLessonsData?.lessons.find((lesson) => {
    return lesson.uuid === lessonUuid;
  });

  const currentLessonsData = lessonsData.find((lesson) => {
    return lesson.uuid === lessonUuid;
  });

  const status = currentLessonsData?.editStatus;

  const initialValues: LessonFormType = lesson
    ? {
        uuid: lesson.uuid,
        subject: lesson.subject,
        lessonClasses: lesson.lessonClassesConnection.edges
          .sort((a, b) => ((a.node.class.grade ?? -1) > (b.node.class.grade ?? -1) ? 1 : -1))
          .map(({ node }) => {
            return {
              uuid: node.uuid,
              groups: node.groups,
              class: {
                uuid: node.class.uuid,
                name: node.class.name,
                color: node.class.timetableConfig?.color,
                grade: node.class.grade,
              },
            };
          }),
        comment: lesson.comment,
        lessonUnit: lesson.lessonUnitConnection.edges.map(({ node }) => {
          return {
            ...node,
            subjectContainerUuid: node.subjectContainer?.uuid,
            blocked: status === 'blocked' || status === 'placedCards',
            lessonUnitType: node.subjectContainer?.uuid ? 'epoch' : 'subjectHour',
          };
        }),
        elective: lesson.elective,
        isClassTrip: lesson.isClassTrip,
        teachingLoadEnabled: lesson.teachingLoadEnabled,
        timetableEnabled: lesson.timetableEnabled,
        teachingLoadInfo: lessonsData.find((l) => l.uuid === lesson.uuid)?.teachingLoadInfo,
        subTitle: lesson.subTitle,
        onlyInTimetableVersion: Boolean(lesson.onlyInTimetableVersion),
        roomSupply: lesson.roomSupplyConnection
          ? lesson.roomSupplyConnection.edges.map((edge) => ({
              uuid: edge.node.uuid,
              description: edge.properties.descriptions ?? '',
            }))
          : [],
        teachers:
          lesson.curriculum && lesson.teachersConnection.edges.length === 0
            ? lesson.curriculum.personsConnection.edges.map((teacherEdge) => {
                return {
                  ...teacherEdge.properties,
                };
              })
            : lesson.teachersConnection.edges.map((teacherEdge) => {
                return {
                  ...teacherEdge.properties,
                  person: {
                    uuid: teacherEdge.node.uuid,
                    fullName: teacherEdge.node.fullName,
                    displayNameShort: teacherEdge.node.displayNameShort ?? teacherEdge.node.fullName,
                    color: teacherEdge.node.timetableConfig?.color,
                  },
                };
              }),
      }
    : {
        lessonClasses: [],
        ...defaultInitialValues,
        teachingLoadEnabled: true,
        timetableEnabled: true,
        lessonUnit: [],
        onlyInTimetableVersion: false,
      };

  const handleSubmit = async (values: LessonFormType, formikHelpers: FormikHelpers<LessonFormType>) => {
    onLoad(true);

    if (!lessonUuid) {
      const results = await createLesson(values);
      if (results.find((r) => r.error)) {
        showUserDbErrorToast();
      } else {
        showSuccessUpdateToast();
      }
    } else {
      const { error } = await updateLesson(values, lessonUuid);
      if (error) {
        showUserDbErrorToast();
      } else {
        showSuccessCreateToast();
      }
    }

    onLoad(false);
    formikHelpers.resetForm();
    onClose();
  };

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
      {({ values, errors, resetForm }) => (
        <Form>
          <Grid useFormGap>
            <GridRow>
              <LessonBasicsForm key={values.subject?.uuid} status={status} />
            </GridRow>
          </Grid>

          {!values.onlyInTimetableVersion && (
            <GridRow headline={t('classes.title', { count: 2 })}>
              <LessonClassesForm disabled={status === 'blocked' || status === 'placedCards'} />
            </GridRow>
          )}

          <GridRow headline={t('lessonUnit.title', { count: 1 })}>
            <LessonUnitForm status={status} />
          </GridRow>

          <GridRow
            headline={
              lesson?.curriculum
                ? `${t('persons.title', { count: 1 })} -> ${values.teachers?.length ?? 0}/${lesson.curriculum.personsConnection.totalCount}`
                : t('persons.title', { count: 1 })
            }
            tooltip={lesson?.curriculum ? t('lesson.personsInCurriculum') : undefined}
          >
            <LessonTeacherForm
              disabled={status === 'blocked'}
              defaults={{ writesCertificate: true, teachingLoad: true, present: true }}
            />
          </GridRow>

          <GridRow headline={t('rooms.stock')}>
            <LessonRoomSupplyForm disabled={status === 'blocked'} />
          </GridRow>

          <ModalBottomButtons
            closeButton={{
              text: status === 'blocked' ? t('common.close') : t('common.cancel'),
              callback: () => {
                resetForm();
                onClose();
              },
            }}
            errors={errors}
          />
        </Form>
      )}
    </Formik>
  );
});
